import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, Sparkles, Shield, Zap, ArrowLeft, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Register = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    email: '',
    profession: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'username' ? value.toLowerCase() : value
    });
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.register.errors.passwordMismatch'));
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.register.errors.passwordTooShort'));
      setLoading(false);
      return;
    }

    const result = await register(formData.name, formData.username, formData.email, formData.profession, formData.password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.message);
    }
    
    setLoading(false);
  };

  const passwordStrength = () => {
    const password = formData.password;
    if (password.length === 0) return 0;
    if (password.length < 6) return 1;
    if (password.length < 8) return 2;
    return 3;
  };

  const getPasswordStrengthColor = () => {
    const strength = passwordStrength();
    if (strength === 0) return 'bg-gray-200';
    if (strength === 1) return 'bg-red-500';
    if (strength === 2) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center p-3 sm:p-4 md:p-6 lg:p-8">
      <div className="w-full max-w-sm sm:max-w-md md:max-w-2xl lg:max-w-5xl bg-white rounded-xl sm:rounded-2xl shadow-xl h-full max-h-[95vh] sm:max-h-[90vh] flex flex-col lg:flex-row">
        {/* Left Side - Content */}
        <div className="lg:w-1/2 bg-gradient-to-br from-indigo-500 to-purple-600 p-4 sm:p-6 md:p-8 flex flex-col justify-center order-2 lg:order-1">
          <div className="text-center lg:text-left">
            <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-white bg-opacity-20 rounded-lg sm:rounded-xl flex items-center justify-center mx-auto lg:mx-0 mb-3 sm:mb-4 lg:mb-6">
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 text-white" />
            </div>
            
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-3xl font-bold text-white mb-2 sm:mb-3 lg:mb-4">
              {t('auth.register.title')}
            </h1>
            
            <p className="text-sm sm:text-base lg:text-lg text-white text-opacity-90 mb-4 sm:mb-6 lg:mb-8 leading-relaxed">
              {t('auth.register.subtitle')}
            </p>
            
            <div className="space-y-2 sm:space-y-3 lg:space-y-4">
              <div className="flex items-center justify-center lg:justify-start space-x-2 sm:space-x-3">
                <div className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <Shield className="w-2.5 h-2.5 sm:w-3 sm:h-3 lg:w-4 lg:h-4 text-white" />
                </div>
                <span className="text-xs sm:text-sm lg:text-base text-white text-opacity-90">Compte gratuit à vie</span>
              </div>
              
              <div className="flex items-center justify-center lg:justify-start space-x-2 sm:space-x-3">
                <div className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <Zap className="w-2.5 h-2.5 sm:w-3 sm:h-3 lg:w-4 lg:h-4 text-white" />
                </div>
                <span className="text-xs sm:text-sm lg:text-base text-white text-opacity-90">Configuration en 5 minutes</span>
              </div>
              
              <div className="flex items-center justify-center lg:justify-start space-x-2 sm:space-x-3">
                <div className="w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <Sparkles className="w-2.5 h-2.5 sm:w-3 sm:h-3 lg:w-4 lg:h-4 text-white" />
                </div>
                <span className="text-xs sm:text-sm lg:text-base text-white text-opacity-90">Support 24/7</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="lg:w-1/2 p-4 sm:p-6 md:p-8 flex items-center justify-center order-1 lg:order-2">
          <div className="w-full max-w-xs sm:max-w-sm">
            <div className="text-center mb-4 sm:mb-6 lg:mb-8">
              <h2 className="text-lg sm:text-xl md:text-2xl lg:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">
                {t('auth.register.title')}
              </h2>
              <p className="text-xs sm:text-sm lg:text-base text-gray-600">
                {t('auth.register.subtitle')}
              </p>
            </div>
            
            <form className="space-y-2 sm:space-y-3 lg:space-y-4" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg flex items-center text-xs sm:text-sm">
                  <div className="w-3 h-3 bg-red-500 rounded-full flex items-center justify-center mr-2">
                    <span className="text-white text-xs">!</span>
                  </div>
                  {error}
                </div>
              )}
              
              <div>
                <label htmlFor="name" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('auth.register.name')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm sm:text-base"
                    placeholder="Votre nom complet"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="username" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('auth.register.username')} *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    required
                    value={formData.username}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm sm:text-base"
                    placeholder="ex: yassine_coiffure"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500 flex items-center">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Lettres, chiffres, tirets et underscores uniquement
                </p>
              </div>

              <div>
                <label htmlFor="profession" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('auth.register.profession')} *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    id="profession"
                    name="profession"
                    type="text"
                    required
                    value={formData.profession}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm sm:text-base"
                    placeholder="ex: Coiffeur, Plombier, Coach..."
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  {t('auth.register.email')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm sm:text-base"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>
              
              {/* Password fields on same line */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 lg:gap-4">
                <div>
                  <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    {t('auth.register.password')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full pl-10 pr-12 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm sm:text-base"
                      placeholder="Au moins 6 caractères"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-600 transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  
                  {/* Password Strength Indicator */}
                  {formData.password && (
                    <div className="mt-1">
                      <div className="flex space-x-1">
                        <div className={`h-1 flex-1 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}></div>
                        <div className={`h-1 flex-1 rounded-full transition-all duration-300 ${passwordStrength() >= 2 ? 'bg-yellow-500' : 'bg-gray-200'}`}></div>
                        <div className={`h-1 flex-1 rounded-full transition-all duration-300 ${passwordStrength() >= 3 ? 'bg-green-500' : 'bg-gray-200'}`}></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {passwordStrength() === 0 && 'Entrez votre mot de passe'}
                        {passwordStrength() === 1 && 'Mot de passe faible'}
                        {passwordStrength() === 2 && 'Mot de passe moyen'}
                        {passwordStrength() === 3 && 'Mot de passe fort'}
                      </p>
                    </div>
                  )}
                </div>
                
                <div>
                  <label htmlFor="confirmPassword" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    {t('auth.register.confirmPassword')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                    </div>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full pl-10 pr-12 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors text-sm sm:text-base"
                      placeholder="Confirmez"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-600 transition-colors"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  
                  {/* Password Match Indicator */}
                  {formData.confirmPassword && (
                    <div className="mt-1 flex items-center">
                      {formData.password === formData.confirmPassword ? (
                        <CheckCircle className="w-3 h-3 text-green-500 mr-1" />
                      ) : (
                        <div className="w-3 h-3 border-2 border-red-300 rounded-full mr-1"></div>
                      )}
                      <span className={`text-xs ${formData.password === formData.confirmPassword ? 'text-green-600' : 'text-red-600'}`}>
                        {formData.password === formData.confirmPassword ? 'Identiques' : 'Différents'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white py-2.5 sm:py-3 px-4 rounded-lg font-medium hover:from-indigo-600 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-lg text-sm sm:text-base"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2 sm:mr-3"></div>
                    {t('common.loading')}
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    {t('auth.register.submit')}
                    <ArrowRight className="ml-2 w-3 h-3 sm:w-4 sm:h-4" />
                  </div>
                )}
              </button>
              
              <div className="text-center">
                <p className="text-xs sm:text-sm text-gray-600">
                  {t('auth.register.hasAccount')}{' '}
                  <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-700 transition-colors">
                    {t('auth.register.login')}
                  </Link>
                </p>
              </div>
            </form>

            <div className="text-center mt-3 sm:mt-4 lg:mt-6">
              <Link 
                to="/" 
                className="inline-flex items-center text-xs sm:text-sm text-gray-500 hover:text-indigo-600 transition-colors"
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                {t('common.back')} {t('navigation.home')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register; 